import type { Tag } from '@/features/tags/public';
import type { PagedResult } from '@/shared/types/pagedResult';
import type { PlannedDate } from '@/shared/types/datetime';

export type TaskStatusType = 'todo' | 'in-progress' | 'done';
export type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';

export interface ProjectStatus {
  id: string;
  name: string;
  type: TaskStatusType;
  color: string;
  order: number;
  boardId: string;
  wipLimit?: number | null;
}

export interface TaskAssignee {
  id: string;
  name: string;
  avatarUrl?: string;
  color: string;
}

// ------------------------------------------------------------------
// API DTOs (aligned with api.json contract)
// ------------------------------------------------------------------

export interface TaskProjectInfoDto {
  id: string;
  name: string;
  color?: string | null;
  icon?: string | null;
}

export interface TaskBoardInfoDto {
  id: string;
  name: string;
}

export interface TaskStatusInfoDto {
  id: string;
  name: string;
  color?: string | null;
  /** Backend sends enum int; legacy strings accepted during migration. */
  type?: number | string | null;
}

export interface TaskGroupInfoDto {
  id: string;
  name: string;
  description?: string | null;
}

export interface TaskUserInfoDto {
  id: string;
  email?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface TaskParentInfoDto {
  id?: string | null;
  title?: string | null;
}

export interface TaskTagDto {
  taskId: string;
  tagId: string;
  createdAt: string;
}

export interface AttachmentDto {
  id: string;
  taskId: string;
  fileName: string;
  storageKey: string;
  fileSize: number;
  mimeType?: string | null;
  uploadedBy?: string | null;
  createdAt: string;
}

export interface FieldValueDto {
  id: string;
  taskId: string;
  fieldId: string;
  value?: string | null;
  updatedAt?: string | null;
}

export interface TaskSummaryDto {
  id: string;
  /** Ids of tags attached to this task — resolve against the project's tag catalog. */
  tagIds?: string[];
  project: TaskProjectInfoDto | null;
  board: TaskBoardInfoDto | null;
  status: TaskStatusInfoDto | null;
  group: TaskGroupInfoDto | null;
  /** Parent task object — API returns TaskParentInfoDto, not full summary. */
  parent?: TaskParentInfoDto | null;
  subtaskCount?: number;
  title: string;
  /** Backend sends enum int; legacy strings accepted during migration. */
  priority?: number | string | null;
  assignee: TaskUserInfoDto | null;
  createdBy: TaskUserInfoDto | null;
  startDate?: PlannedDate | null;
  dueDate?: PlannedDate | null;
  estimatedHours?: number | null;
  totalTrackedHours?: number | null;
  isCompleted: boolean;
  isArchived?: boolean;
  order: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TaskDetailDto extends TaskSummaryDto {
  description?: string | null;
}

export type TaskSummaryDtoPagedResult = PagedResult<TaskSummaryDto>;

export interface MyPendingTasksParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

export interface PendingTaskDto {
  id: string;
  title: string;
  startDate: PlannedDate | null;
  dueDate: PlannedDate | null;
  totalTrackedHours: number;
  statusName: string;
  statusColor: string;
}

export type PendingTaskDtoPagedResult = PagedResult<PendingTaskDto>;

/**
 * `TaskActivityEntry.ActionType` — see `catalogo-eventos-actividad-tareas.md` for the exact
 * fields each value populates (`targetName`/`oldValue`/`newValue`/`metadata`). Task deletion
 * never produces an entry, by backend design. `comment_added`/`comment_deleted` and
 * `ai_task_created` were added for the board-scoped activity feed (project-activity-tab feature).
 */
export type TaskActivityActionType =
  | 'task_created'
  | 'ai_task_created'
  | 'task_updated'
  | 'task_moved'
  | 'task_group_changed'
  | 'task_assigned'
  | 'task_priority_changed'
  | 'task_toggled'
  | 'task_blocked_reason_changed'
  | 'task_archived'
  | 'task_unarchived'
  | 'subtask_added'
  | 'task_dependency_added'
  | 'task_dependency_removed'
  | 'tag_added'
  | 'tag_removed'
  | 'attachment_uploaded'
  | 'attachment_deleted'
  | 'field_value_upserted'
  | 'checklist_created'
  | 'checklist_renamed'
  | 'checklist_deleted'
  | 'checklist_item_added'
  | 'checklist_item_updated'
  | 'checklist_item_toggled'
  | 'checklist_item_deleted'
  | 'checklist_item_moved'
  | 'checklist_item_converted'
  | 'comment_added'
  | 'comment_deleted';

export interface ActivityDto {
  id: string | null;
  /** Present on board-scoped activity (`GetBoardActivity`); `null` on the per-task endpoint, which is always scoped to one task already. */
  taskId: string | null;
  /** The task's title at the time the activity was logged (denormalized — stale after a later rename). Populated on every row, added 2026-08-27 specifically so the board feed can show a real task name next to the "ver tarea" link instead of relying on `targetName`, whose meaning varies per `actionType`. */
  taskTitle: string | null;
  /** `string` rather than `TaskActivityActionType` — an unrecognized/future value must not break parsing, only fall back to a generic message. */
  actionType: string | null;
  /** One of `TaskActivityCategory`; computed server-side. `null` on the per-task endpoint, which predates this field. */
  category: string | null;
  actorId: string | null;
  actorName: string | null;
  /** Actor's *current* profile photo URL — resolved live at read time, not denormalized like `actorName`. `null` if the user has no photo set. */
  actorAvatarUrl: string | null;
  /** Actor's *current* persisted avatar color — resolved live at read time, not denormalized. `null` if never set; fall back to the app's default avatar-color derivation. */
  actorColor: string | null;
  targetName: string | null;
  oldValue: string | null;
  newValue: string | null;
  /** Opaque JSON string (same convention as `TaskApiParams.filters`/`.sort`) — parse with `parseActivityMetadata`. */
  metadata: string | null;
  createdAt: string;
}

export interface ActivityDtoPagedResult {
  items: ActivityDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface TaskActivityParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

/** `TaskActivityCategoryMapper`'s output — `ActivityDto.category` on board-scoped activity. */
export const TASK_ACTIVITY_CATEGORY = {
  Comment: 'comment',
  StatusChange: 'status_change',
  Assignment: 'assignment',
  Attachment: 'attachment',
  General: 'general',
} as const;

export type TaskActivityCategory =
  (typeof TASK_ACTIVITY_CATEGORY)[keyof typeof TASK_ACTIVITY_CATEGORY];

/**
 * `GET /projects/{projectId}/boards/{boardId}/activity` query params.
 * `category` accepts only the 4 filterable values (`general` is reachable only by omitting it).
 * Casing matches the wire contract exactly (`api.json`): `Page`/`PageSize` are PascalCase
 * (shared `PagedRequest` binding), `category` is lowercase (its own dedicated binding) — not a typo.
 */
export interface BoardActivityParams {
  Page?: number;
  PageSize?: number;
  category?: Exclude<TaskActivityCategory, 'general'>;
}

/** `GET .../activity/summary` query params — both required by the API; same value for a single-day "today" view. `From`/`To` casing matches `api.json` (`[AsParameters]` binding), unlike the feed endpoint's lowercase `category`. */
export interface BoardActivityDateRangeParams {
  From: string;
  To: string;
}

export interface ActiveUserDto {
  id: string;
  name: string | null;
  avatarUrl: string | null;
  color: string | null;
}

export interface PersonActivityCountDto {
  userId: string;
  name: string | null;
  avatarUrl: string | null;
  color: string | null;
  count: number;
}

export interface BoardActivitySummaryDto {
  totalChanges: number;
  completedTasks: number;
  newComments: number;
  mostActiveUser: ActiveUserDto | null;
  perPerson: PersonActivityCountDto[] | null;
}

// ------------------------------------------------------------------
// API Request types
// ------------------------------------------------------------------

export interface CreateTaskApiRequest {
  title?: string | null;
  description?: string | null;
  boardId?: string | null;
  statusId?: string | null;
  groupId?: string | null;
  parentId?: string | null;
  priority?: number;
  assigneeId?: string | null;
  startDate?: PlannedDate | null;
  dueDate?: PlannedDate | null;
  estimatedHours?: number | null;
}

export interface UpdateTaskApiRequest {
  title?: string | null;
  description?: string | null;
  startDate?: PlannedDate | null;
  dueDate?: PlannedDate | null;
  estimatedHours?: number | null;
}

export interface MoveTaskApiRequest {
  statusId?: string | null;
  groupId?: string | null;
  afterTaskId?: string | null;
}

export interface AssignTaskApiRequest {
  assigneeId?: string | null;
}

export interface ChangeTaskPriorityApiRequest {
  priority: number;
}

export interface ReorderTasksApiRequest {
  boardId?: string | null;
  statusId?: string | null;
  orderedTaskIds?: string[] | null;
}

export interface CreateSubtaskApiRequest {
  title?: string | null;
  description?: string | null;
}

export interface UpsertFieldValueApiRequest {
  value?: string | null;
}

// ------------------------------------------------------------------
// UI types
// ------------------------------------------------------------------

export interface TaskAttachment {
  id: string;
  taskId: string;
  name: string;
  url: string;
  thumbnailUrl?: string | null;
  type: 'image' | 'document' | 'other';
  size: number;
  uploadedAt: string;
  uploadedBy?: TaskAssignee | null;
}

export interface TaskTimerState {
  seconds: number;
  running: boolean;
}

export interface Task {
  id: string;
  title: string;
  /** Ids of tags attached to this task — resolve against the project's tag catalog. */
  tagIds?: string[];
  description?: string;
  status: ProjectStatus;
  priority?: TaskPriority;
  assignee: TaskAssignee | null;
  dueDate?: PlannedDate | null;
  startDate?: PlannedDate | null;
  estimatedMinutes?: number | null;
  totalTrackedHours?: number | null;
  orgId?: string;
  projectId?: string;
  groupId?: string;
  parentId?: string | null;
  subtaskCount?: number;
  subtasks?: Task[];
  isCompleted: boolean;
  isArchived?: boolean;
  order: number;
}

export interface TaskTreeNode {
  id: string | null;
  title: string | null;
  subtaskCount: number;
  children: TaskTreeNode[] | null;
}

export interface TaskDetail extends Task {
  tags: Tag[];
  attachments: TaskAttachment[];
  estimatedMinutes: number | null;
  timer: TaskTimerState;
  createdBy: TaskAssignee | null;
  createdAt: string;
  updatedAt?: string;
}

export interface TaskGroup {
  id: string;
  name: string;
  color: string;
  projectId: string;
}

export interface TaskListData {
  tasks: Task[];
  groups: TaskGroup[];
}

export interface CreateTaskPayload {
  title: string;
  boardId: string;
  description?: string;
  statusId?: string;
  groupId?: string | null;
  parentId?: string;
  priority?: TaskPriority;
  assigneeId?: string;
  dueDate?: PlannedDate | null;
  startDate?: PlannedDate | null;
  estimatedMinutes?: number | null;
}

export type UpdateTaskPayload = Partial<CreateTaskPayload>;

export interface UpdateTaskStatusPayload {
  statusId: string;
  order?: number;
}

export interface CreateStatusPayload {
  name: string;
  type: TaskStatusType;
  color: string;
}

export interface UpdateTaskDetailPayload {
  title?: string;
  description?: string;
  priority?: TaskPriority | null;
  statusId?: string;
  groupId?: string | null;
  startDate?: PlannedDate | null;
  dueDate?: PlannedDate | null;
  assigneeId?: string | null;
  estimatedMinutes?: number | null;
}

export interface CreateActivityPayload {
  label: string;
}

export interface UpdateActivityPayload {
  label?: string;
  completed?: boolean;
}

export interface CreateSubtaskPayload {
  name: string;
}

export interface UpdateSubtaskPayload {
  name?: string;
  assigneeId?: string | null;
  priority?: TaskPriority;
  dueDate?: PlannedDate | null;
  startDate?: PlannedDate | null;
  statusId?: string;
  estimatedMinutes?: number | null;
}

export interface AttachTagPayload {
  tagId: string;
}

export interface TaskRelatedData {
  children: Task[];
  related: Task[];
}

// ------------------------------------------------------------------
// Comments
// ------------------------------------------------------------------

export interface TaskCommentDto {
  id: string | null;
  taskId: string | null;
  parentId: string | null;
  authorId: string | null;
  authorName: string | null;
  authorAvatarUrl: string | null;
  content: string | null;
  isDeleted: boolean;
  createdAt: string;
}

export interface TaskCommentDtoPagedResult {
  items: TaskCommentDto[] | null;
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
}

export interface TaskCommentParams {
  Page?: number;
  PageSize?: number;
  SearchTerm?: string;
  SortBy?: string;
  SortAscending?: boolean;
}

export interface AddTaskCommentPayload {
  content: string;
  parentId?: string | null;
}

export type FeedItem =
  | { kind: 'activity'; data: ActivityDto }
  | { kind: 'comment'; data: TaskCommentDto; replies: TaskCommentDto[] };

// ------------------------------------------------------------------
// Copy task
// ------------------------------------------------------------------

export interface CopyTaskApiRequest {
  includeTags: boolean;
  includeFieldValues: boolean;
  includeChecklists: boolean;
  includeSubtasks: boolean;
}

export interface CopyTaskResponse {
  taskId: string;
}

// ------------------------------------------------------------------
// Bulk edit tasks
// ------------------------------------------------------------------

export interface BulkEditTasksApiRequest {
  boardId: string;
  taskIds: string[];
  statusName?: string | null;
  groupName?: string | null;
  assigneeEmail?: string | null;
  clearAssignee?: boolean;
  priority?: number | null;
  dueDate?: PlannedDate | null;
  startDate?: PlannedDate | null;
}

export interface BulkEditTaskResult {
  taskId: string;
  success: boolean;
  error: string | null;
}

export interface BulkEditTasksResponse {
  results: BulkEditTaskResult[];
}

// ------------------------------------------------------------------
// Bulk delete tasks
// ------------------------------------------------------------------

export interface BulkDeleteTasksApiRequest {
  taskIds: string[];
}

export interface BulkDeleteTaskResult {
  taskId: string;
  success: boolean;
  error: string | null;
}

export interface BulkDeleteTasksResponse {
  results: BulkDeleteTaskResult[];
}

// ------------------------------------------------------------------
// Import tasks from file
// ------------------------------------------------------------------

export interface ImportTaskError {
  rowIndex: number;
  reason: string;
}

export interface ImportTasksResponse {
  created: string[];
  errors: ImportTaskError[];
}
